import React, { createContext, useContext, useState, useCallback } from "react";

const translations = {
  de: {
    hero_headline: "Willkommen zum Clarity-Zones-Kommunikationsprofil",
    hero_subtext:
      "Dieses Profil hilft dabei, die eigene Kommunikations- und Informationslogik besser zu verstehen. Es zeigt keine Persönlichkeit und bewertet keine Fähigkeiten, sondern macht sichtbar, wie Informationen typischerweise ausgewählt, strukturiert und vermittelt werden.",
    hero_instruction:
      "Bitte beantworten Sie die Fragen aus Ihrer typischen Arbeitsweise heraus – nicht abhängig von einzelner Situation oder Zielgruppe. Wenn mehrere Antworten passen, wählen Sie bitte die Option, die Ihrem typischen Vorgehen am ehesten entspricht.",
    name_label: "Name (optional, nur für das Titelblatt des PDF-Reports)",
    name_placeholder: "Ihr Name",
    start_button: "Assessment starten",
    question_of: "Frage {current} von {total}",
    back: "Zurück",
    next: "Weiter",
    submit: "Auswertung anzeigen",
    results_title: "Ihr Kommunikationsprofil",
    zones_title: "Zonen-Ausprägung",
    facets_title: "Dimensionsprofil",
    pathos_logos_title: "Wirkungsstil",
    text_modules_title: "Interpretation",
    download_pdf: "PDF herunterladen",
    pdf_generating: "PDF wird erstellt…",
    pdf_error: "Die PDF konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
    download_hint: "Hinweis: Ihre Daten werden nicht gespeichert. Bitte laden Sie den Report als PDF herunter, bevor Sie die Seite verlassen.",
    restart: "Neues Assessment",
    vision: "Vision",
    alignment: "Alignment",
    deep_dive: "Deep Dive",
    bridge: "Bridge",
    dominant: "Dominant",
    balanced: "Ausgewogen",
    low: "Gering",
    pathos: "Pathos",
    logos: "Logos",
    emotional: "Emotional",
    rational: "Rational",
    effect_balanced: "Ausgewogen",
    facet_abstraktion: "Abstraktionspräferenz",
    facet_ziel: "Ziel- vs. Prozessfokus",
    facet_entscheid: "Entscheidungsstil",
    facet_adressat: "Adressatenfokus",
    facet_struktur: "Strukturbedürfnis",
    facet_argument: "Argumentationspräferenz",
    facet_unsicherheit: "Unsicherheitstoleranz",
    facet_wirkung: "Wirkungsfokus",
    facet_low: "Gering",
    facet_mid: "Mittel",
    facet_high: "Hoch",
    application_title: "Anwendung",
    zone_insights_title: "Zonen-Einordnung",
    bullet_title: "Kurzinterpretation",
    ethos_title: "Ethos",
    primary_label: "Primäre Zone",
    secondary_label: "Sekundäre Zone",
    score_label: "Score",
    lang_toggle: "EN",
    preparing: "Ergebnis wird berechnet...",
    facet_left_abstraktion: "Detailorientiert",
    facet_right_abstraktion: "Holistisch",
    facet_left_ziel: "Prozess",
    facet_right_ziel: "Ziel",
    facet_left_entscheid: "Analytisch",
    facet_right_entscheid: "Intuitiv",
    facet_left_adressat: "Intern",
    facet_right_adressat: "Extern",
    facet_left_struktur: "Flexibel",
    facet_right_struktur: "Strukturiert",
    facet_left_argument: "Faktenbasiert",
    facet_right_argument: "Narrativ",
    facet_left_unsicherheit: "Sicherheitsorientiert",
    facet_right_unsicherheit: "Ambiguitätstolerant",
    facet_left_wirkung: "Ergebnisorientiert",
    facet_right_wirkung: "Beziehungsorientiert",
    // Voucher login
    voucher_title: "Zugang einlösen",
    voucher_subtitle: "Geben Sie Ihre Zugangsdaten ein, um das Assessment zu starten.",
    voucher_alias_label: "Zugangs-Alias",
    voucher_alias_placeholder: "z.B. bright-eagle-42",
    voucher_password_label: "Passwort",
    voucher_password_placeholder: "Ihr Passwort",
    voucher_login_button: "Zugang einlösen",
    voucher_hint: "Sie erhalten Ihre Zugangsdaten von Ihrem Trainer, Coach oder Veranstalter. Jeder Zugang ist für eine einmalige Nutzung vorgesehen.",
    voucher_invalid: "Zugang ungültig",
    voucher_redeemed: "Dieser Zugang wurde bereits eingelöst.",
    voucher_greeting: "Angemeldet als",
    voucher_onetime_notice: "Dieser Zugang ist für die einmalige Nutzung vorgesehen. Bitte laden Sie Ihren Report als PDF herunter.",
  },
  en: {
    hero_headline: "Welcome to the Clarity Zones Communication Profile",
    hero_subtext:
      "This profile helps you better understand your own communication and information logic. It does not show personality and does not evaluate abilities, but makes visible how information is typically selected, structured, and conveyed.",
    hero_instruction:
      "Please answer the questions based on your typical working style – not dependent on a specific situation or audience. If multiple answers apply, please choose the option that best matches your typical approach.",
    name_label: "Name (optional, only for the PDF report cover page)",
    name_placeholder: "Your name",
    start_button: "Start Assessment",
    question_of: "Question {current} of {total}",
    back: "Back",
    next: "Next",
    submit: "Show Results",
    results_title: "Your Communication Profile",
    zones_title: "Zone Scores",
    facets_title: "Dimension Profile",
    pathos_logos_title: "Impact Style",
    text_modules_title: "Interpretation",
    download_pdf: "Download PDF",
    pdf_generating: "Generating PDF…",
    pdf_error: "The PDF could not be generated. Please try again.",
    download_hint: "Note: Your data is not saved. Please download the report as PDF before leaving this page.",
    restart: "New Assessment",
    vision: "Vision",
    alignment: "Alignment",
    deep_dive: "Deep Dive",
    bridge: "Bridge",
    dominant: "Dominant",
    balanced: "Balanced",
    low: "Low",
    pathos: "Pathos",
    logos: "Logos",
    emotional: "Emotional",
    rational: "Rational",
    effect_balanced: "Balanced",
    facet_abstraktion: "Abstraction Preference",
    facet_ziel: "Goal vs. Process Focus",
    facet_entscheid: "Decision Style",
    facet_adressat: "Audience Focus",
    facet_struktur: "Structure Need",
    facet_argument: "Argumentation Preference",
    facet_unsicherheit: "Uncertainty Tolerance",
    facet_wirkung: "Impact Focus",
    facet_low: "Low",
    facet_mid: "Mid",
    facet_high: "High",
    application_title: "Application",
    zone_insights_title: "Zone Insights",
    bullet_title: "Key Interpretation",
    ethos_title: "Ethos",
    primary_label: "Primary Zone",
    secondary_label: "Secondary Zone",
    score_label: "Score",
    lang_toggle: "DE",
    preparing: "Calculating results...",
    facet_left_abstraktion: "Detail-Oriented",
    facet_right_abstraktion: "Holistic",
    facet_left_ziel: "Process",
    facet_right_ziel: "Goal",
    facet_left_entscheid: "Analytical",
    facet_right_entscheid: "Intuitive",
    facet_left_adressat: "Internal",
    facet_right_adressat: "External",
    facet_left_struktur: "Flexible",
    facet_right_struktur: "Structured",
    facet_left_argument: "Fact-Based",
    facet_right_argument: "Narrative",
    facet_left_unsicherheit: "Security-Oriented",
    facet_right_unsicherheit: "Ambiguity-Tolerant",
    facet_left_wirkung: "Result-Oriented",
    facet_right_wirkung: "Relationship-Oriented",
    // Voucher login
    voucher_title: "Redeem Access",
    voucher_subtitle: "Enter your access credentials to start the assessment.",
    voucher_alias_label: "Access Alias",
    voucher_alias_placeholder: "e.g. bright-eagle-42",
    voucher_password_label: "Password",
    voucher_password_placeholder: "Your password",
    voucher_login_button: "Redeem Access",
    voucher_hint: "You receive your credentials from your trainer, coach, or event organizer. Each access is intended for one-time use.",
    voucher_invalid: "Invalid access",
    voucher_redeemed: "This access has already been redeemed.",
    voucher_greeting: "Logged in as",
    voucher_onetime_notice: "This access is intended for one-time use. Please download your report as PDF.",
  },
};

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [lang, setLang] = useState("de");

  const t = useCallback(
    (key, params) => {
      let text = translations[lang]?.[key] || translations.de[key] || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(`{${k}}`, v);
        });
      }
      return text;
    },
    [lang]
  );

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === "de" ? "en" : "de"));
  }, []);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, toggleLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
